import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api.js';
import { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from '../config/index.js';
import { setLiveCapabilities } from '../config/routeAccess.js';

const AuthContext = createContext(null);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

function persistSession(user, token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function clearSession() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem(USER_STORAGE_KEY);
}

/**
 * CAPABILITIES ARE NOT CACHED IN localStorage, AND THAT IS DELIBERATE.
 *
 * The user object is cached so the app can paint something while /auth/me is
 * in flight. Capabilities must not be: a cached list is by definition a guess
 * about what the server will allow now, and an administrator can revoke a
 * capability between two page loads. Unknown capabilities render a loading
 * state (see ProtectedRoute); they never render an optimistic guess.
 */

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /**
   * `null` means NOT YET KNOWN — never "holds nothing". Every consumer must
   * distinguish the two, which is why `capabilitiesLoading` exists alongside
   * it and why `can()` returns false only once the list has actually arrived.
   */
  const [capabilities, setCapabilities] = useState(null);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  const [capabilitiesError, setCapabilitiesError] = useState(null);

  // Guards the "did this component go away mid-request" case and lets the
  // 403 listener below re-enter without racing itself.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Reads the caller's effective capabilities from the server. `background`
   * refreshes (after a 403) keep the current list visible while re-reading, so
   * the app does not flash a loading state under the user for a request that
   * failed elsewhere.
   */
  const loadCapabilities = useCallback(async ({ background = false } = {}) => {
    if (!background) setCapabilitiesLoading(true);
    try {
      const result = await api.myPermissions();
      if (!mounted.current) return null;
      const set = new Set(result?.capabilities || []);
      // Published to the module-level register BEFORE the state update, so the
      // components that re-render because of it already see the same answer
      // through canAccessRoute() (config/routeAccess.js) — the two can never
      // disagree about the same session.
      setLiveCapabilities(set);
      setCapabilities(set);
      setCapabilitiesError(null);
      return set;
    } catch (err) {
      if (!mounted.current) return null;
      // A failed read leaves capabilities UNKNOWN, not empty — the guards
      // surface that honestly rather than guessing in either direction.
      setLiveCapabilities(null);
      setCapabilities(null);
      setCapabilitiesError(err);
      return null;
    } finally {
      if (mounted.current && !background) setCapabilitiesLoading(false);
    }
  }, []);

  const clearCapabilities = useCallback(() => {
    setLiveCapabilities(null);
    setCapabilities(null);
    setCapabilitiesError(null);
    setCapabilitiesLoading(false);
  }, []);

  // On mount, restore the cached user and re-validate the token against the
  // API. Capabilities are fetched alongside — a session without them is a
  // session that does not yet know what it may open.
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    const cached = localStorage.getItem(USER_STORAGE_KEY);

    if (!token) {
      setLoading(false);
      setCapabilitiesLoading(false);
      return;
    }

    if (cached) {
      try {
        setUser(JSON.parse(cached));
      } catch {
        clearSession();
      }
    }

    api
      .me()
      .then((fresh) => {
        setUser(fresh);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(fresh));
        return loadCapabilities();
      })
      .catch(() => {
        clearSession();
        setUser(null);
        clearCapabilities();
      })
      .finally(() => setLoading(false));
  }, [loadCapabilities, clearCapabilities]);

  /**
   * The server is the only authority on access. When it refuses a request the
   * client thought was allowed, the client's list is stale — re-read it rather
   * than leaving the two disagreeing. This never grants anything: it replaces
   * one server-issued list with a newer server-issued list.
   */
  useEffect(() => {
    const onForbidden = () => {
      if (!localStorage.getItem(TOKEN_STORAGE_KEY)) return;
      loadCapabilities({ background: true });
    };
    window.addEventListener('acuteos:forbidden', onForbidden);
    return () => window.removeEventListener('acuteos:forbidden', onForbidden);
  }, [loadCapabilities]);

  const login = useCallback(
    async (email, password) => {
      const { user: u, token } = await api.login({ email, password });
      persistSession(u, token);
      setUser(u);
      // Awaited on purpose: by the time the sign-in page navigates, the app
      // already knows what this account may open, so the first authenticated
      // render is never a guess and never a flash of /403.
      await loadCapabilities();
      return u;
    },
    [loadCapabilities]
  );

  // Registration never signs the caller in — the account is created
  // `pending_verification` and stays that way until a network administrator
  // approves it (see backend auth.service.js). Returns the enumeration-safe
  // `{ status, message }` confirmation payload for the caller to display.
  const register = useCallback(async (payload) => api.register(payload), []);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
    clearCapabilities();
  }, [clearCapabilities]);

  // Password recovery — none of these touch the session; see
  // pages/forgot-password and pages/reset-password.
  const forgotPassword = useCallback(async (email) => api.forgotPassword(email), []);
  const validateResetToken = useCallback(async (token) => api.validateResetToken(token), []);
  const resetPassword = useCallback(async (payload) => api.resetPassword(payload), []);

  // Unlike reset-password, this keeps the caller signed in — the backend
  // always returns a freshly-signed token (its token_version may or may not
  // have moved, depending on revokeOtherSessions), so swap the stored
  // session for it rather than logging out. The role is unchanged, so the
  // capability list still holds.
  const changePassword = useCallback(async (payload) => {
    const result = await api.changePassword(payload);
    if (result?.user && result?.token) {
      persistSession(result.user, result.token);
      setUser(result.user);
    }
    return result;
  }, []);

  // Same token-swap contract as changePassword: bumping token_version on the
  // server invalidates this device's JWT too, so the fresh one that comes
  // back must replace the stored session or the next request 401s.
  const revokeOtherSessions = useCallback(async () => {
    const result = await api.revokeOtherSessions();
    if (result?.user && result?.token) {
      persistSession(result.user, result.token);
      setUser(result.user);
    }
    return result;
  }, []);

  /**
   * `can('ledger-explorer', 'view')` — true only when the server has said so.
   * While the list is unknown this returns false, so no caller can ever infer
   * "allowed" from missing data; callers that must not act on an unknown
   * (route guards, menus) check `capabilitiesKnown` first.
   */
  const can = useCallback(
    (pageSlug, capability = 'view') => {
      if (!capabilities) return false;
      return capabilities.has(`${pageSlug}:${capability}`);
    },
    [capabilities]
  );

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      capabilities,
      capabilitiesKnown: !!capabilities,
      capabilitiesLoading,
      capabilitiesError,
      refreshCapabilities: loadCapabilities,
      can,
      login,
      register,
      logout,
      forgotPassword,
      validateResetToken,
      resetPassword,
      changePassword,
      revokeOtherSessions,
    }),
    [
      user,
      loading,
      capabilities,
      capabilitiesLoading,
      capabilitiesError,
      loadCapabilities,
      can,
      login,
      register,
      logout,
      forgotPassword,
      validateResetToken,
      resetPassword,
      changePassword,
      revokeOtherSessions,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
